/**
 * «Записи» catalogue, part 4 — what the child opens to read and hear (owner 27.09: «поставь все фразы на озвучку»): the
 * concept cards, the opening repertoire, the game review (its greeting, hint and the answer of a moment — the move
 * itself is never said: the board's arrows show it). Read-aloud lines are `long` (./lint.ts `CLIP_LONG_CAPS`): a card is
 * two lines, each one TTS job. One line per meaning; placeholders: see ./catalog.ts.
 *
 * Every wording here is exactly what its bubble says, so a line has ONE wording (only the interchangeable openers of a
 * review moment have a few): the cards and the openings mirror @gambit/content by id, the review explanations mirror
 * core analysis/summary.ts word for word — catalog.read.test.ts keeps them equal, and the web's tests (ConceptCardModal,
 * OpeningModal, reviewMoment) check that every bubble is exactly its lines. New wordings are APPENDED to a line, never
 * inserted (a wording's number is its recording's key).
 */
import type { ClipCatalogLine, ClipWording } from '@gambit/shared';

const w = (...texts: string[]): ClipWording[] => texts.map((t) => ({ t }));

// ───────────────────────── concept cards (apps/web ConceptCardModal) ─────────────────────────

/**
 * A concept card said on opening: `.a` its title and first sentence(s), `.b` the rest — split at a sentence end so that
 * each half keeps one idea. A title with a digit is said in words («Золотое правило номер один»).
 */
function card(id: string, a: string, b: string): ClipCatalogLine[] {
  return [
    { id: `read.card.${id}.a`, role: 'whole', long: true, wordings: w(a) },
    { id: `read.card.${id}.b`, role: 'whole', long: true, wordings: w(b) },
  ];
}

const CARDS: readonly ClipCatalogLine[] = [
  // stage 1: sharp eye
  ...card(
    'piece-values',
    'Сколько стоят фигуры. У каждой фигуры своя цена. Пешка — один, конь и слон — по три, ладья — пять, ферзь — девять.',
    'Король бесценен: его нельзя менять. Когда есть выбор, бери то, что дороже.',
  ),
  ...card(
    'hanging-piece',
    'Незащищённая фигура. Фигура, которую никто не защищает, «висит». На неё напали — и её можно забрать бесплатно.',
    'Перед своим ходом проверь: все ли твои фигуры под защитой? А у соперника что-нибудь висит?',
  ),
  ...card(
    'free-capture',
    'Забери бесплатное. Соперник иногда оставляет фигуру под боем и забывает её защитить. Это подарок!',
    'Сначала проверь, что тебя не съедят в ответ. Если никто не защищает — смело бери.',
  ),
  ...card(
    'bad-trade',
    'Выгодный и невыгодный размен. Размен — это когда мы едим фигуру, а нашу едят в ответ. Посчитай: что я отдаю и что получаю?',
    'Отдать коня за ладью — выгодно. Отдать ферзя за коня — очень невыгодно. Поровну — решай по позиции.',
  ),
  ...card(
    'thinking-routine',
    'Три вопроса перед ходом. Мастера не спешат: перед каждым ходом они задают себе три вопроса.',
    'Что хочет соперник? Что могу я: шахи, взятия, угрозы? Безопасен ли мой ход? Запомни: стоп — смотрю — считаю — проверяю — хожу!',
  ),
  ...card(
    'mate-in-1',
    'Мат в один ход. Мат — это шах, от которого нет спасения. Сначала смотри все шахи.',
    'Потом проверь три двери: может ли король уйти, можно ли закрыться, можно ли съесть нападающего. Все двери закрыты? Это мат!',
  ),
  // stage 2: mate and the three golden rules
  ...card(
    'opening-center',
    'Золотое правило номер один: пешкой в центр. Центр — это четыре клетки посередине доски.',
    'Кто владеет центром, тому просторно, а его фигуры быстро попадают куда угодно. Поэтому партию начинаем ходом центральной пешки на две клетки.',
  ),
  ...card(
    'opening-development',
    'Золотое правило номер два: разбуди фигуры. В начале партии фигуры спят дома.',
    'Разбуди коней и слонов: каждым ходом выводи новую фигуру. Не ходи одной фигурой дважды без причины. Армия сильна, когда играют все.',
  ),
  ...card(
    'opening-king-safety',
    'Золотое правило номер три: спрячь короля. Король в центре — в опасности: там открываются линии.',
    'Рокировка делает сразу два дела. Король прячется за пешки, а ладья выходит в игру. Постарайся сделать рокировку до десятого хода.',
  ),
  ...card(
    'opening-early-queen',
    'Не выводи ферзя слишком рано. Ферзь — самая дорогая фигура. Если он выйдет рано, на него будут нападать кони и слоны соперника.',
    'Ферзь будет убегать, а чужие фигуры — развиваться. А если рано вышел ферзь соперника — нападай на него с пользой!',
  ),
  ...card(
    'scholars-mate',
    'Детский мат. Рядом с королём в начале игры есть слабая клетка: её защищает только сам король. Ферзь и слон вдвоём могут ударить туда и поставить мат за четыре хода.',
    'Защититься легко, если вовремя заметить. Закрой дорогу ферзю конём или пешкой.',
  ),
  ...card(
    'back-rank-mate',
    'Мат на последней линии. Король спрятался за тремя пешками. Это хорошо, но есть опасность: если на последнюю линию ворвётся ладья или ферзь, королю некуда бежать — мешают свои же пешки.',
    'Чтобы не попасться самому, вовремя сделай королю форточку.',
  ),
  ...card(
    'stalemate',
    'Пат — это ничья. Пат — это когда у соперника нет ни одного хода, но шаха ему нет. Это ничья, даже если у тебя лишний ферзь!',
    'Когда у соперника остался один король, перед каждым ходом проверяй: есть ли у него хоть один ход?',
  ),
  ...card(
    'endgame-ladder-mate',
    'Мат двумя ладьями: «лесенка». Две ладьи ставят мат без помощи короля. Одна ладья закрывает королю линию, как забор.',
    'Вторая даёт шах на следующей линии. Так, ступенька за ступенькой, короля оттесняют на край доски. Там его ждёт мат.',
  ),
  ...card(
    'endgame-queen-mate',
    'Мат ферзём и королём: «коробочка». Один ферзь мат не поставит — ему нужен помощник, король. Ферзь строит вокруг чужого короля коробочку и делает её всё меньше.',
    'Когда король соперника прижат к краю, подводим своего короля. И только потом ставим мат. Берегись пата!',
  ),
  ...card(
    'endgame-rook-mate',
    'Мат ладьёй и королём. Ладья и король ставят мат только на краю доски. Наш король встаёт напротив чужого, через одну клетку, — и тогда ладья даёт шах по краю.',
    'Если короли ещё не стоят напротив, сделай выжидательный ход и подожди.',
  ),
  // stage 3 and later: tactics and endgames
  ...card(
    'fork',
    'Вилка. Вилка — это когда одна фигура нападает сразу на две. Соперник может спасти только одну, вторую мы забираем.',
    'Лучше всех вилки ставит конь, но умеют все — даже пешка. Особенно сильна вилка с шахом.',
  ),
  ...card(
    'pin',
    'Связка. Связка — это когда фигура не может уйти, потому что за ней стоит кто-то важнее. Если сзади король, уходить вообще запрещено.',
    'Связанная фигура как приклеенная: она плохо защищает и плохо защищается. На неё хорошо напасть ещё раз, лучше всего пешкой.',
  ),
  ...card(
    'skewer',
    'Сквозной удар. Сквозной удар похож на связку наоборот. Мы нападаем на важную фигуру, чаще всего шахом на короля.',
    'Она обязана уйти. А за ней на той же линии стоит другая фигура — её мы и забираем.',
  ),
  ...card(
    'mate-in-2',
    'Мат в два хода. Мат в два хода — это маленький план: мой ход, ответ соперника, мой мат.',
    'Начинай с шахов: на шах соперник обязан ответить, и угадать его ход легко. Считай в уме: я — он — я. И проверь все его ответы.',
  ),
  ...card(
    'endgame-square-rule',
    'Правило квадрата. Успеет ли король догнать пешку? Считать ходы не нужно. Нарисуй в уме квадрат: от пешки до клетки превращения и столько же в сторону.',
    'Если король при своём ходе входит в квадрат — он догонит пешку. Если нет — пешка убежит в ферзи.',
  ),
  ...card(
    'endgame-opposition',
    'Оппозиция. Оппозиция — это когда короли стоят друг напротив друга через одну клетку. Тот, кому сейчас ходить, должен уступить дорогу.',
    'С королём и пешкой против короля помни: король идёт впереди пешки и расчищает ей путь. А защищаясь, вставай напротив чужого короля.',
  ),
  ...card(
    'discovered-attack',
    'Вскрытое нападение. Две наши фигуры стоят на одной линии. Передняя отходит — и задняя вдруг нападает на чужую фигуру.',
    'Если передняя при этом сама создаёт угрозу или даёт шах, получаются два нападения сразу. От двух угроз одним ходом не защититься.',
  ),
  ...card(
    'double-check',
    'Двойной шах. Двойной шах — это шах сразу от двух фигур. Одна фигура отходит с шахом и открывает шах от второй.',
    'От двух шахов нельзя закрыться и нельзя съесть обоих. Королю остаётся только убегать. А если бежать некуда — это мат.',
  ),
  ...card(
    'remove-defender',
    'Уничтожение защитника. Фигуру соперника нельзя забрать, потому что её кто-то защищает?',
    'Тогда сначала убери защитника! Съешь его или прогони. Без охраны фигура остаётся беззащитной, и мы её забираем.',
  ),
  ...card(
    'deflection',
    'Отвлечение. У каждого защитника есть работа: он что-то охраняет. Отвлечение — это когда мы заставляем его бросить свой пост.',
    'Чаще всего помогает шах или жертва: защитник вынужден съесть нашу фигуру. А то, что он сторожил, достаётся нам.',
  ),
  ...card(
    'attraction',
    'Завлечение. Завлечение — это приманка. Мы жертвуем фигуру, чтобы чужой король или ферзь встал на плохую клетку.',
    'А на этой клетке его уже ждёт вилка, связка или мат. Отдаём немного — получаем больше.',
  ),
  ...card(
    'trapped-piece',
    'Ловля фигуры. Фигура, у которой нет безопасных клеток, попала в ловушку. Чаще всего так попадаются жадные фигуры, которые забрались далеко за пешкой, и кони на краю доски.',
    'Сначала закрой фигуре все выходы. Потом спокойно напади на неё.',
  ),
  ...card(
    'mate-in-3',
    'Мат в три хода. Мат в три хода — это уже настоящий счёт вариантов. Ищи ходы, на которые у соперника мало ответов: шахи и жертвы.',
    'Считай в уме: я — он — я — он — мат. Не двигай фигуры, пока не досчитаешь до конца.',
  ),
  ...card(
    'promotion',
    'Превращение пешки. Пешка, дошедшая до последней линии, превращается в любую фигуру, кроме короля. Обычно выбирают ферзя — он самый сильный.',
    'Чем ближе пешка к концу доски, тем она опаснее. Иногда выгоднее превратить пешку в коня: например, чтобы поставить вилку.',
  ),
  ...card(
    'smothered-mate',
    'Спёртый мат. Спёртый мат ставит конь, когда король со всех сторон окружён своими же фигурами. Королю душно: бежать некуда, а от шаха конём закрыться нельзя.',
    'Часто перед этим нужно пожертвовать ферзя, чтобы чужая фигура заняла последнюю свободную клетку.',
  ),
];

// ───────────────────────── the opening repertoire (apps/web OpeningModal) ─────────────────────────

/** A part of the repertoire on opening (`read.opening.<entry>`: title + summary), a model line chosen (`.<line>`: title + idea). */
const opening = (id: string, text: string): ClipCatalogLine => ({ id: `read.opening.${id}`, role: 'whole', long: true, wordings: w(text) });

const OPENINGS: readonly ClipCatalogLine[] = [
  opening(
    'italian-white',
    'Итальянская партия. Самый понятный дебют для открытой игры: пешка в центр, конь, слон — и сразу рокировка. Слон с первых ходов смотрит на слабую клетку возле чёрного короля.',
  ),
  opening('italian-white.italian-quiet', 'Тихая итальянка. Никаких ловушек: развиваем все фигуры, прячем короля и готовим удар в центре. Конь идёт длинным путём на королевский фланг.'),
  opening('italian-white.italian-strong-center', 'Сильный центр. Строим две пешки в центре. Если чёрные меняются, у наших фигур открываются линии.'),
  opening('italian-white.italian-two-knights-quiet', 'Если чёрные вывели второго коня. Спокойно защищаем центральную пешку и продолжаем развитие. Получается та же тихая итальянка.'),
  opening('italian-white.white-vs-sicilian', 'Против сицилианской защиты. Тот же план «сильный центр»: готовим шаг ферзевой пешки на две клетки.'),
  opening('italian-white.white-vs-french', 'Против французской защиты. Меняемся в центре и получаем открытую игру с простым развитием.'),
  opening('italian-white.white-vs-scandinavian', 'Против скандинавской защиты. Бьём пешку, а потом конь выходит с нападением на ферзя. Мы развиваемся, а ферзь убегает.'),
  opening(
    'e5-black',
    'Чёрными: отвечаем пешкой в центр. Зеркало нашего белого дебюта: ставим пешку в центр, выводим коней и слонов, делаем рокировку. Мы учим одни и те же позиции с обеих сторон.',
  ),
  opening('e5-black.black-vs-italian', 'Против итальянской партии. Развиваемся зеркально: слон, конь, скромный шаг ферзевой пешки и рокировка.'),
  opening('e5-black.black-vs-italian-center', 'Белые строят центр — бьём по нему. Меняем пешку, даём шах слоном и наносим ответный удар ферзевой пешкой в центре.'),
  opening('e5-black.black-vs-knight-attack', 'Против выпада конём: без «жареной печени». Закрываемся ударом пешки в центре. Пешку конём не берём, а уводим коня на край с нападением на слона.'),
  opening(
    'e5-black.black-vs-early-queen',
    'Против раннего ферзя. Сначала защищаем центральную пешку. Потом закрываем угрозу мата пешкой, а следующую угрозу — конём. Ферзь белых бегает, а мы развиваемся.',
  ),
  opening('e5-black.black-vs-spanish', 'Против испанской партии. Прогоняем слона пешками, развиваем фигуры и делаем рокировку. Центральную пешку держим.'),
  opening('e5-black.black-vs-scotch', 'Против шотландской партии. Бьём пешку в центре и развиваемся с темпом: каждый наш ход что-то атакует.'),
  opening(
    'd5-black',
    'Чёрными: крепкая пешка в центре. Надёжная расстановка отказанного ферзевого гамбита: пешка в центре под защитой соседки, конь, слон, рокировка. Никаких приключений — просто крепкая позиция.',
  ),
  opening('d5-black.black-qgd-setup', 'Основная расстановка. Пешка в центре под защитой, конь, слон, рокировка, второй конь. Потом — удар по центру.'),
  opening('d5-black.black-vs-london', 'Против лондонской системы. Слон белых ушёл со своего места, и пешка рядом с ним ослабла. Бьём по центру и нападаем на неё ферзём.'),
  opening('d5-black.black-vs-flank', 'Против фланговых начал. Та же расстановка: пешки в центр, конь, слон, рокировка.'),
  opening(
    'd5-black.black-greedy-pawn-trap',
    'Почему нельзя жадничать. Чёрные взяли пешку и пытаются её удержать. Белые вскрывают линию, и ферзь нападает на ладью в углу — чёрные теряют фигуру.',
  ),
];

// ───────────────────────── the game review (apps/web features/review) ─────────────────────────

const REVIEW: readonly ClipCatalogLine[] = [
  // the greeting: «Давай посмотрим партию!», then how many moments Гамбитик marked (in words) — or how to leaf through
  { id: 'review.hello', role: 'whole', wordings: w('Давай посмотрим партию!') },
  { id: 'review.hello.browse', role: 'whole', wordings: w('Листай ходы стрелками.') },
  { id: 'review.hello.one', role: 'whole', wordings: w('Я отметил один интересный момент. Попробуешь найти ходы сильнее?') },
  { id: 'review.hello.two', role: 'whole', wordings: w('Я отметил два интересных момента. Попробуешь найти ходы сильнее?') },
  { id: 'review.hello.three', role: 'whole', wordings: w('Я отметил три интересных момента. Попробуешь найти ходы сильнее?') },
  { id: 'review.hello.four', role: 'whole', wordings: w('Я отметил четыре интересных момента. Попробуешь найти ходы сильнее?') },
  // (a game marks at most four — three to learn from, one to be proud of; «несколько» keeps any other count honest)
  { id: 'review.hello.many', role: 'whole', wordings: w('Я отметил несколько интересных моментов. Попробуешь найти ходы сильнее?') },
  // «Подсказка» of a moment: the piece is lit yellow, never where it goes
  { id: 'review.hint', role: 'whole', wordings: w('Посмотри на эту фигуру. Куда она может пойти с пользой?') },
  // the answer of a moment: an opener (the board's arrows show the move), then its explanation (`review.why.*`)
  { id: 'review.found', role: 'whole', wordings: w('Нашёлся! Вот он, сильный ход.', 'Точно! Теперь этот ход твой.', 'Есть! Вот это работа головой!') },
  { id: 'review.revealed', role: 'whole', wordings: w('Смотри, какой ход тут прятался.', 'Вот что нашёл компьютер.', 'Давай посмотрим вместе.') },
  { id: 'review.proud', role: 'whole', wordings: w('Вот этим ходом можно гордиться!', 'Смотри, какой сильный ход был в партии!') },
  // the child's own good move that is not the computer's favourite: the review board draws it blue, the favourite green
  { id: 'review.alt', role: 'whole', wordings: w('Это тоже хороший ход!', 'Отличная находка — так тоже можно!') },
  { id: 'review.alt.best', role: 'whole', wordings: w('А компьютеру больше всего нравится ход по зелёной стрелке.') },
];

/**
 * The explanation of a key moment, word for word as core analysis/summary.ts writes it (`KEY_MOMENT_EXPLANATIONS_RU`,
 * `hangingPieceExplanationRu`): what the move allowed, what it missed, what a proud move did, the fallbacks. The web
 * finds a moment's line by its words (features/review/reviewMoment.ts).
 */
const WHY: readonly ClipCatalogLine[] = [
  // the move allowed …
  { id: 'review.why.allowed.hanging', role: 'whole', wordings: w('Фигура осталась без защиты, и соперник мог её забрать.') },
  // (the piece the refutation takes first; while that piece has no take, its sibling above says it without the piece)
  {
    id: 'review.why.allowed.piece',
    role: 'whole',
    byPiece: true,
    fallback: 'review.why.allowed.hanging',
    wordings: w('{Конь} {p:остался|осталась} под боем — соперник мог {его} забрать.'),
  },
  { id: 'review.why.allowed.badTrade', role: 'whole', wordings: w('Размен вышел невыгодным: отдали больше, чем взяли.') },
  { id: 'review.why.allowed.fork', role: 'whole', long: true, wordings: w('После хода в партии соперник мог напасть сразу на две фигуры — это вилка.') },
  { id: 'review.why.allowed.pin', role: 'whole', wordings: w('После хода в партии фигура попадала под связку и не могла уйти.') },
  { id: 'review.why.allowed.skewer', role: 'whole', long: true, wordings: w('Соперник мог напасть на важную фигуру, а за ней стояла ещё одна — это сквозной удар.') },
  { id: 'review.why.allowed.discoveredAttack', role: 'whole', wordings: w('Соперник мог отойти фигурой и открыть нападение — это вскрытое нападение.') },
  { id: 'review.why.allowed.doubleCheck', role: 'whole', wordings: w('Соперник мог объявить двойной шах — от него можно только убегать королём.') },
  { id: 'review.why.allowed.removeDefender', role: 'whole', wordings: w('Соперник мог убрать защитника, и фигура осталась бы без охраны.') },
  { id: 'review.why.allowed.trappedPiece', role: 'whole', wordings: w('Фигуру могли поймать: ей некуда было отступить.') },
  { id: 'review.why.allowed.backRankMate', role: 'whole', wordings: w('Королю некуда было убежать с последней линии — грозил мат. Помогает «форточка».') },
  { id: 'review.why.allowed.mateIn1', role: 'whole', wordings: w('После хода в партии соперник мог поставить мат в один ход.') },
  { id: 'review.why.allowed.mateIn2', role: 'whole', wordings: w('После хода в партии соперник мог поставить мат в два хода.') },
  { id: 'review.why.allowed.mateIn3', role: 'whole', wordings: w('После хода в партии соперник мог поставить мат в три хода.') },
  { id: 'review.why.allowed.promotion', role: 'whole', wordings: w('Пешка соперника могла пройти в ферзи.') },
  // … the move missed
  { id: 'review.why.missed.freeCapture', role: 'whole', wordings: w('Здесь можно было забрать фигуру бесплатно.') },
  { id: 'review.why.missed.hangingPiece', role: 'whole', wordings: w('Здесь можно было выиграть фигуру: её защищали слишком слабо.') },
  { id: 'review.why.missed.fork', role: 'whole', wordings: w('Здесь была вилка — можно было напасть сразу на две фигуры.') },
  { id: 'review.why.missed.pin', role: 'whole', wordings: w('Здесь можно было выиграть связанную фигуру: она не могла уйти.') },
  { id: 'review.why.missed.skewer', role: 'whole', long: true, wordings: w('Здесь был сквозной удар: важная фигура уходит, а та, что за ней, остаётся под боем.') },
  { id: 'review.why.missed.discoveredAttack', role: 'whole', wordings: w('Здесь можно было отойти фигурой и открыть нападение.') },
  { id: 'review.why.missed.doubleCheck', role: 'whole', wordings: w('Здесь был двойной шах королю — очень сильный приём.') },
  { id: 'review.why.missed.removeDefender', role: 'whole', wordings: w('Здесь можно было сначала убрать защитника, а потом забрать фигуру.') },
  { id: 'review.why.missed.trappedPiece', role: 'whole', wordings: w('Здесь можно было поймать фигуру соперника: ей некуда отступать.') },
  { id: 'review.why.missed.backRankMate', role: 'whole', wordings: w('Здесь был мат по последней линии!') },
  { id: 'review.why.missed.mateIn1', role: 'whole', wordings: w('Здесь был мат в один ход!') },
  { id: 'review.why.missed.mateIn2', role: 'whole', wordings: w('Здесь был мат в два хода.') },
  { id: 'review.why.missed.mateIn3', role: 'whole', wordings: w('Здесь был мат в три хода.') },
  { id: 'review.why.missed.promotion', role: 'whole', wordings: w('Здесь пешка могла пройти в ферзи.') },
  // a move to be proud of
  { id: 'review.why.found.freeCapture', role: 'whole', wordings: w('Отлично замечено: фигура соперника стояла без защиты — и теперь она твоя!') },
  { id: 'review.why.found.hangingPiece', role: 'whole', wordings: w('Отлично замечено: фигуру защищали слабо — и она выиграна!') },
  { id: 'review.why.found.fork', role: 'whole', wordings: w('Красивая вилка — нападение сразу на две фигуры!') },
  { id: 'review.why.found.pin', role: 'whole', wordings: w('Здорово использована связка!') },
  { id: 'review.why.found.skewer', role: 'whole', wordings: w('Отличный сквозной удар!') },
  { id: 'review.why.found.discoveredAttack', role: 'whole', wordings: w('Классное вскрытое нападение!') },
  { id: 'review.why.found.doubleCheck', role: 'whole', wordings: w('Двойной шах королю — очень сильный ход!') },
  { id: 'review.why.found.removeDefender', role: 'whole', wordings: w('Умно: сначала убран защитник, а потом забрана фигура.') },
  { id: 'review.why.found.trappedPiece', role: 'whole', wordings: w('Фигура соперника поймана — отличная работа!') },
  { id: 'review.why.found.backRankMate', role: 'whole', wordings: w('Мат по последней линии — супер!') },
  { id: 'review.why.found.mateIn1', role: 'whole', wordings: w('Мат найден — супер!') },
  { id: 'review.why.found.mateIn2', role: 'whole', wordings: w('Найден мат в два хода — здорово!') },
  { id: 'review.why.found.mateIn3', role: 'whole', wordings: w('Найден мат в три хода — это очень сильно!') },
  { id: 'review.why.found.promotion', role: 'whole', wordings: w('Пешка прошла в ферзи — отлично!') },
  { id: 'review.why.found.other', role: 'whole', wordings: w('Отличный ход — лучший в этой позиции!') },
  // no motif text: a missed win, lost material, a neutral fact
  { id: 'review.why.missedWin', role: 'whole', wordings: w('Тут был подарок от соперника — можно было получить большой перевес.') },
  { id: 'review.why.material', role: 'whole', wordings: w('Ход в партии терял материал. Перед ходом проверь: всем фигурам спокойно?') },
  { id: 'review.why.other', role: 'whole', wordings: w('Другой ход давал позицию покрепче.') },
];

/**
 * The words of games saved before 27.09: «После этого хода…» right after «Теперь этот ход твой.» read as if the FOUND move
 * were meant, so summary.ts now says «После хода в партии…»; a review of an older game still reads its saved words —
 * these lines keep them voiced (`LEGACY_EXPLANATIONS_RU` of catalog.read.test.ts).
 */
const WHY_LEGACY: readonly ClipCatalogLine[] = [
  { id: 'review.why.legacy.fork', role: 'whole', wordings: w('После этого хода соперник мог напасть сразу на две фигуры — это вилка.') },
  { id: 'review.why.legacy.pin', role: 'whole', wordings: w('После этого хода фигура попадала под связку и не могла уйти.') },
  { id: 'review.why.legacy.mateIn1', role: 'whole', wordings: w('После этого хода соперник мог поставить мат в один ход.') },
  { id: 'review.why.legacy.mateIn2', role: 'whole', wordings: w('После этого хода соперник мог поставить мат в два хода.') },
  { id: 'review.why.legacy.mateIn3', role: 'whole', wordings: w('После этого хода соперник мог поставить мат в три хода.') },
  { id: 'review.why.legacy.material', role: 'whole', wordings: w('После этого хода терялся материал. Перед ходом проверь: всем фигурам спокойно?') },
];

/**
 * The path's own words of a key moment (`words: 'path'`, a game of «Путь пешки» 2.0), word for word as core
 * analysis/summary.ts writes them (`PATH_KEY_MOMENT_EXPLANATIONS_RU`, `pathCapturePieceExplanationRu`): «подарок /
 * даром» only for an `isGift` capture, «охраняли мало» for a piece won in an exchange, the glossary of stages 1–2
 * (PATH-V2 §3.2, §12.3). Where the path says what the classic review says, the classic line above is it. The web finds a
 * line by its words (features/review/reviewMoment.ts), so these are voiced like the rest; recorded with the path's
 * campaign (`--pools '…,review.why.path.*'`) or on demand.
 */
const WHY_PATH: readonly ClipCatalogLine[] = [
  // the move allowed … (a piece the refutation takes first: its own words; while it has no take, the plain sibling)
  { id: 'review.why.path.allowed.gift', role: 'whole', wordings: w('Фигура осталась без охраны, и соперник мог забрать её даром.') },
  {
    id: 'review.why.path.allowed.giftPiece',
    role: 'whole',
    byPiece: true,
    fallback: 'review.why.path.allowed.gift',
    wordings: w('{Конь} {p:остался|осталась} без охраны — соперник мог забрать {его} даром.'),
  },
  { id: 'review.why.path.allowed.guarded', role: 'whole', wordings: w('Фигуру охраняли мало, и соперник мог её выиграть.') },
  {
    id: 'review.why.path.allowed.guardedPiece',
    role: 'whole',
    byPiece: true,
    fallback: 'review.why.path.allowed.guarded',
    wordings: w('{Коня} охраняли мало — соперник мог {его} выиграть.'),
  },
  { id: 'review.why.path.allowed.badTrade', role: 'whole', wordings: w('Мы отдали больше, чем взяли.') },
  { id: 'review.why.path.allowed.discoveredAttack', role: 'whole', wordings: w('Соперник мог отойти одной фигурой — и тогда другая доставала нашу.') },
  { id: 'review.why.path.allowed.removeDefender', role: 'whole', long: true, wordings: w('Соперник мог сначала забрать того, кто охраняет, — и фигура осталась бы без охраны.') },
  // … the move missed
  { id: 'review.why.path.missed.gift', role: 'whole', wordings: w('Здесь был подарок: фигуру можно было забрать даром.') },
  { id: 'review.why.path.missed.guarded', role: 'whole', wordings: w('Здесь можно было выиграть фигуру: её охраняли мало.') },
  { id: 'review.why.path.missed.discoveredAttack', role: 'whole', long: true, wordings: w('Здесь можно было отойти одной фигурой — и тогда другая доставала фигуру соперника.') },
  { id: 'review.why.path.missed.removeDefender', role: 'whole', wordings: w('Здесь можно было сначала забрать того, кто охраняет, а потом и фигуру.') },
  { id: 'review.why.path.missedWin', role: 'whole', wordings: w('Тут был ход сильнее — можно было получить большой перевес.') },
  // a move to be proud of
  { id: 'review.why.path.found.gift', role: 'whole', wordings: w('Отлично замечено: фигура соперника стояла без охраны — подарок твой!') },
  { id: 'review.why.path.found.guarded', role: 'whole', wordings: w('Отлично замечено: фигуру охраняли мало — и она выиграна!') },
  { id: 'review.why.path.found.fork', role: 'whole', wordings: w('Красивая вилка: напали сразу на две фигуры!') },
  { id: 'review.why.path.found.discoveredAttack', role: 'whole', wordings: w('Классно: одна фигура отошла — и другая сразу достаёт!') },
  { id: 'review.why.path.found.removeDefender', role: 'whole', wordings: w('Умно: сначала забрана охрана, а потом и фигура.') },
];

export const READ_CATALOG: readonly ClipCatalogLine[] = [...CARDS, ...OPENINGS, ...REVIEW, ...WHY, ...WHY_LEGACY, ...WHY_PATH];
